// PrivacyPolicy.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './LegalPages.css';
import logo from '../images/logo.png';

function PrivacyPolicy() {
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem('user'));
  const user_id = user?.user_id;

  return (
    <div className="legal-page light-theme">
      {/* Navigation */}
      <nav className="legal-navbar">
        <div className="legal-nav-container">
          <button
            className="legal-brand"
            onClick={() => navigate(user_id ? '/home' : '/')}
          >
            <img src={logo} alt='Marshall AI Logo' className='legal-logo' />
            <span>Marshall AI</span>
          </button>
          <button
            className="legal-back-btn"
            onClick={() => navigate(-1)}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Back
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <div className="legal-container">
        <div className="legal-content">
          <h1 className="legal-title">Privacy Policy</h1>
          <p className="legal-last-updated">Last Updated: April 12, 2026</p>

          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              Welcome to Marshall AI ("we," "our," or "us"). We are committed to protecting your privacy 
              and ensuring the security of your personal information. This Privacy Policy explains how we 
              collect, use, disclose, and safeguard your information when you use our AI chat application.
            </p>
            <p>
              By accessing or using Marshall AI, you agree to the terms outlined in this Privacy Policy. 
              If you do not agree with our policies and practices, please do not use our services.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Information We Collect</h2>
            <h3>2.1 Personal Information</h3>
            <p>We may collect the following types of personal information:</p>
            <ul>
              <li><strong>Account Information:</strong> Name, email address, username, and profile picture when you create an account.</li>
              <li><strong>Authentication Data:</strong> Information from Google OAuth if you choose to sign in with Google.</li>
              <li><strong>Communication Data:</strong> Messages, queries, and conversations you have with our AI assistant.</li>
              <li><strong>Uploaded Content:</strong> Images and files you upload for analysis or processing.</li>
            </ul>

            <h3>2.2 Automatically Collected Information</h3>
            <ul>
              <li><strong>Usage Data:</strong> Information about how you interact with our services.</li>
              <li><strong>Device Information:</strong> Browser type, operating system, and device identifiers.</li>
              <li><strong>Log Data:</strong> IP address, access times, and pages viewed.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use the collected information for the following purposes:</p>
            <ul>
              <li>To provide, maintain, and improve our AI chat services</li>
              <li>To process and respond to your queries and requests</li>
              <li>To personalize your experience and remember your preferences</li>
              <li>To communicate with you about updates, security alerts, and support messages</li>
              <li>To analyze usage patterns and improve our AI models</li>
              <li>To detect, prevent, and address technical issues or security breaches</li>
              <li>To comply with legal obligations and enforce our terms</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. Data Storage and Security</h2>
            <p>
              We implement appropriate technical and organizational security measures to protect your 
              personal information against unauthorized access, alteration, disclosure, or destruction.
            </p>
            <ul>
              <li>All data is encrypted in transit using TLS/SSL protocols</li>
              <li>Passwords are hashed and never stored in plain text</li>
              <li>We regularly review our security practices and update them as needed</li>
              <li>Access to personal data is restricted to authorized personnel only</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>5. Data Sharing and Disclosure</h2>
            <p>We do not sell, trade, or rent your personal information to third parties. We may share information in the following circumstances:</p>
            <ul>
              <li><strong>Service Providers:</strong> With trusted third-party vendors who assist us in operating our services.</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental regulation.</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
              <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>6. Your Rights and Choices</h2>
            <p>Depending on your location, you may have the following rights:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of your personal data.</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information.</li>
              <li><strong>Deletion:</strong> Request deletion of your account and associated data.</li>
              <li><strong>Data Portability:</strong> Receive your data in a structured, machine-readable format.</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications.</li>
            </ul>
            <p>To exercise these rights, please contact us at privacy@marshallai.com.</p>
          </section>

          <section className="legal-section">
            <h2>7. Cookies and Tracking Technologies</h2>
            <p>
              We use cookies and similar tracking technologies to enhance your browsing experience, 
              analyze site traffic, and understand user behavior. For more details, please refer to our 
              <a href="/cookies" className="legal-link"> Cookie Policy</a>.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Children's Privacy</h2>
            <p>
              Marshall AI is not intended for children under the age of 13. We do not knowingly collect 
              personal information from children under 13. If you believe we have inadvertently collected 
              such information, please contact us immediately.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country 
              of residence. We ensure appropriate safeguards are in place to protect your data in 
              accordance with applicable privacy laws.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any significant 
              changes by posting the new policy on this page and updating the "Last Updated" date. 
              Your continued use of our services after such changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Contact Us</h2>
            <p>If you have any questions or concerns about this Privacy Policy, please contact us:</p>
            <ul>
              <li><strong>Email:</strong> c84289798@gmail.com</li>
              <li><strong>Subject Line:</strong> Privacy Policy Inquiry</li>
              <li><strong>Response Time:</strong> We aim to respond within 48 hours</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;